"use strict";
export const HTML_IMPORT_ZIP_TEXT_PREVIEW_MAX_CHARS = 5e5;
const HTML_FILE_EXTENSIONS = /* @__PURE__ */ new Set(["html", "htm"]);
const IMAGE_PREVIEW_EXTENSIONS = /* @__PURE__ */ new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
  "ico",
  "bmp",
  "tif",
  "tiff"
]);
const VIDEO_PREVIEW_EXTENSIONS = /* @__PURE__ */ new Set(["mp4", "webm", "ogv", "mov", "m4v", "avi", "mkv"]);
const AUDIO_PREVIEW_EXTENSIONS = /* @__PURE__ */ new Set(["mp3", "wav", "m4a", "flac", "aac", "opus", "ogg"]);
const TEXT_PREVIEW_EXTENSIONS = /* @__PURE__ */ new Set([
  "css",
  "js",
  "mjs",
  "cjs",
  "json",
  "txt",
  "md",
  "xml",
  "map",
  "less",
  "scss",
  "sass",
  "ts",
  "tsx",
  "jsx",
  "vue",
  "yml",
  "yaml",
  "csv",
  "log",
  "sh",
  "sql",
  "styl",
  "coffee",
  "rb",
  "py",
  "java",
  "c",
  "h",
  "cpp",
  "hpp",
  "go",
  "rs",
  "php"
]);
export function htmlImportZipIsHtmlPath(path) {
  return /\.(html|htm)$/i.test(path.split("?")[0] ?? path);
}
export function zipImportPathExtension(path) {
  const base = (path.split(/[?#]/)[0] ?? path).replace(/\\/g, "/").split("/").pop() ?? "";
  const i = base.lastIndexOf(".");
  return i >= 0 ? base.slice(i + 1).toLowerCase() : "";
}
export function htmlImportZipMimeType(path) {
  const ext = zipImportPathExtension(path);
  switch (ext) {
    case "html":
    case "htm":
      return "text/html";
    case "css":
      return "text/css";
    case "js":
    case "mjs":
    case "cjs":
      return "text/javascript";
    case "json":
      return "application/json";
    case "svg":
      return "image/svg+xml";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "bmp":
      return "image/bmp";
    case "avif":
      return "image/avif";
    case "ico":
      return "image/x-icon";
    case "woff2":
      return "font/woff2";
    case "woff":
      return "font/woff";
    case "ttf":
      return "font/ttf";
    case "otf":
      return "font/otf";
    case "eot":
      return "application/vnd.ms-fontobject";
    case "mp4":
      return "video/mp4";
    case "webm":
      return "video/webm";
    case "mov":
      return "video/quicktime";
    case "m4v":
      return "video/x-m4v";
    case "ogv":
      return "video/ogg";
    case "ogg":
      return "audio/ogg";
    case "mkv":
      return "video/x-matroska";
    case "avi":
      return "video/x-msvideo";
    case "mp3":
      return "audio/mpeg";
    case "wav":
      return "audio/wav";
    case "m4a":
      return "audio/mp4";
    case "flac":
      return "audio/flac";
    case "aac":
      return "audio/aac";
    case "opus":
      return "audio/opus";
    default:
      return "application/octet-stream";
  }
}
export function htmlImportZipAssetPreviewKind(path) {
  const ext = zipImportPathExtension(path);
  if (!path || HTML_FILE_EXTENSIONS.has(ext)) return "html";
  if (IMAGE_PREVIEW_EXTENSIONS.has(ext)) return "image";
  if (VIDEO_PREVIEW_EXTENSIONS.has(ext)) return "video";
  if (AUDIO_PREVIEW_EXTENSIONS.has(ext)) return "audio";
  if (TEXT_PREVIEW_EXTENSIONS.has(ext)) return "text";
  return "binary";
}
export const HTML_IMPORT_ZIP_FOLDER_HI_ICON = { kind: "folder", material: true };
export function zipEntryFileHiIconSpec(path) {
  const base = (path.split(/[?#]/)[0] ?? path).replace(/\\/g, "/");
  const seg = base.split("/").pop() ?? base;
  const lower = seg.toLowerCase();
  if (lower === ".ds_store" || lower.startsWith("._")) return { kind: "description", material: true };
  if (/\.(html|htm)$/i.test(lower)) return { kind: "insert_drive_file", material: true };
  if (/\.css$/i.test(lower)) return { kind: "insert_drive_file", material: true };
  if (/\.(js|mjs|cjs)$/i.test(lower)) return { kind: "insert_drive_file", material: true };
  if (/\.(png|jpe?g|gif|webp|svg|ico|bmp|avif)$/i.test(lower)) return { kind: "image", material: true };
  if (/\.(mp4|webm|mov|m4v|ogv|mkv|avi)$/i.test(lower)) return { kind: "movie", material: true };
  return { kind: "description" };
}
function zipTreeSiblingSortOrder(node) {
  if (node.fullPath === void 0) return 1;
  if (/\.(html|htm)$/i.test(node.name)) return 0;
  return 2;
}
function sortHtmlImportZipPathTreeChildren(nodes) {
  nodes.sort((a, b) => {
    const byKind = zipTreeSiblingSortOrder(a) - zipTreeSiblingSortOrder(b);
    if (byKind !== 0) return byKind;
    return a.name.localeCompare(b.name, void 0, { sensitivity: "base" });
  });
  for (const n of nodes) sortHtmlImportZipPathTreeChildren(n.children);
}
export function singleRootDirectoryPrefix(paths) {
  if (paths.length === 0) return "";
  const normalized = paths.map((p) => p.replace(/\\/g, "/").replace(/^\.\/+/, ""));
  const firstSegs = normalized.map((p) => {
    const i = p.indexOf("/");
    return i === -1 ? p : p.slice(0, i);
  });
  const head = firstSegs[0];
  if (!head || !firstSegs.every((s) => s === head)) return "";
  const prefix = `${head}/`;
  if (!normalized.every((p) => p === head || p.startsWith(prefix))) return "";
  if (normalized.some((p) => p === head)) return "";
  return prefix;
}
export function buildHtmlImportZipPathTree(paths, stripPrefix = "") {
  const roots = [];
  for (const path of paths) {
    const norm = path.replace(/\\/g, "/").replace(/^\.\/+/, "");
    const logical = stripPrefix && norm.startsWith(stripPrefix) ? norm.slice(stripPrefix.length) : norm;
    const segments = logical.split("/").filter((s) => s.length > 0);
    if (segments.length === 0) continue;
    let siblings = roots;
    let prefix = "";
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const isLeaf = i === segments.length - 1;
      prefix = prefix ? `${prefix}/${seg}` : seg;
      const dirKey = `dir:${prefix}`;
      let node = siblings.find((n) => n.name === seg);
      if (!node) {
        node = {
          key: isLeaf ? path : dirKey,
          name: seg,
          fullPath: isLeaf ? path : void 0,
          children: []
        };
        siblings.push(node);
      } else if (isLeaf) {
        node.fullPath = path;
        node.key = path;
      }
      siblings = node.children;
    }
  }
  sortHtmlImportZipPathTreeChildren(roots);
  return roots;
}
